import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import FormBuilderPage from './pages/FormBuilderPage';
import FormFillPage from './pages/FormFillPage';
import DepartmentsPage from './pages/DepartmentsPage';
import SubmissionsPage from './pages/SubmissionsPage';
import CrmConnectionsPage from './pages/CrmConnectionsPage';
import CrmMappingPage from './pages/CrmMappingPage';
import CrmJobsPage from './pages/CrmJobsPage';
import LineBotPage from './pages/LineBotPage';
import CalendarPage from './pages/CalendarPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import ContactsPage from './pages/ContactsPage';
import LeadFormPage from './pages/LeadFormPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import PipelinePage from './pages/PipelinePage';
import MyDealsPage from './pages/MyDealsPage';
import VelopulseShell from './components/VelopulseShell';
import OrgSwitchPage from './pages/OrgSwitchPage';
import WorkbenchPage from './pages/WorkbenchPage';
import RecordsLibraryPage from './pages/RecordsLibraryPage';
import RecordDetailPage from './pages/RecordDetailPage';
import OpportunityBoardPage from './pages/OpportunityBoardPage';
import OpportunityDetailPage from './pages/OpportunityDetailPage';
import LineBotWizardPage from './pages/LineBotWizardPage';
import AssistantPage from './pages/AssistantPage';
import SettingsHubPage from './pages/SettingsHubPage';
import RealtyTaxPage from './pages/RealtyTaxPage';
import RealtyTitleDeedPage from './pages/RealtyTitleDeedPage';
import RealtySearchPage from './pages/RealtySearchPage';
import PlateLookupPage from './pages/auto/PlateLookupPage';
import OrgAdminShell, { OrgAdminGate, UsersToOrgAdminRedirect, NoAccessPage } from './components/OrgAdminShell';
import OrgAdminOverviewPage from './pages/orgAdmin/OrgAdminOverviewPage';
import OrgAdminMembersPage from './pages/orgAdmin/OrgAdminMembersPage';
import OrgAdminSettingsPage from './pages/orgAdmin/OrgAdminSettingsPage';
import InviteAcceptPage from './pages/InviteAcceptPage';
import PlatformShell, { PlatformGate } from './components/PlatformShell';
import PlatformCompaniesPage from './pages/platform/PlatformCompaniesPage';
import PlatformCompanyDetailPage from './pages/platform/PlatformCompanyDetailPage';
import RequireModule from './components/RequireModule';

const ProtectedRoute = ({ children, roles, permission }) => {
  const { token, user, hasPermission } = useAuthStore();
  if (!token) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user?.role)) return <Navigate to="/" replace />;
  if (permission && !hasPermission(permission)) return <Navigate to="/" replace />;
  return children;
};

export default function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" toastOptions={{
        style: { fontFamily: 'Noto Sans TC, sans-serif', fontSize: '14px' }
      }} />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/lead/:token/:formId" element={<LeadFormPage />} />
        <Route path="/lead/:token" element={<LeadFormPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/invite/:token" element={<InviteAcceptPage />} />
        <Route path="/orgs" element={<ProtectedRoute><OrgSwitchPage /></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute><UsersToOrgAdminRedirect /></ProtectedRoute>} />
        <Route path="/no-access" element={<ProtectedRoute><NoAccessPage /></ProtectedRoute>} />
        <Route path="/platform" element={<ProtectedRoute><PlatformGate><PlatformShell /></PlatformGate></ProtectedRoute>}>
          <Route index element={<PlatformCompaniesPage />} />
          <Route path="companies/:id" element={<PlatformCompanyDetailPage />} />
        </Route>
        <Route path="/org-admin" element={<ProtectedRoute><OrgAdminGate><OrgAdminShell /></OrgAdminGate></ProtectedRoute>}>
          <Route index element={<OrgAdminOverviewPage />} />
          <Route path="members" element={<OrgAdminMembersPage />} />
          <Route path="settings" element={<OrgAdminSettingsPage />} />
        </Route>
        <Route path="/" element={<ProtectedRoute><VelopulseShell /></ProtectedRoute>}>
          {/* Velopulse R1：工作台／紀錄／商機／管道／助理 */}
          <Route index element={<WorkbenchPage />} />
          <Route path="records" element={<RequireModule module="automotive" title="尚未開通賣車功能"><RecordsLibraryPage /></RequireModule>} />
          <Route path="records/:id" element={<RequireModule module="automotive" title="尚未開通賣車功能"><RecordDetailPage /></RequireModule>} />
          <Route path="opportunities" element={<RequireModule anyOf={["automotive","real_estate"]} title="尚未開通商機功能"><OpportunityBoardPage /></RequireModule>} />
          <Route path="opportunities/:id" element={<RequireModule anyOf={["automotive","real_estate"]} title="尚未開通商機功能"><OpportunityDetailPage /></RequireModule>} />
          <Route path="channels/line/wizard" element={<LineBotWizardPage />} />
          <Route path="assistant" element={<AssistantPage />} />
          <Route path="realty/tax" element={<RequireModule module="real_estate" title="尚未開通賣房功能"><RealtyTaxPage /></RequireModule>} />
          <Route path="realty/title-deed" element={<RequireModule module="real_estate" title="尚未開通賣房功能"><RealtyTitleDeedPage /></RequireModule>} />
          <Route path="realty/search" element={<RequireModule module="real_estate" title="尚未開通賣房功能"><RealtySearchPage /></RequireModule>} />
          <Route path="auto/plates" element={<RequireModule module="automotive" title="尚未開通賣車功能"><PlateLookupPage /></RequireModule>} />
          <Route path="settings" element={<SettingsHubPage />} />
          <Route path="more" element={<SettingsHubPage more />} />
          {/* 既有功能：保留原路由，入口收在「設定」；表單總覽由 / 移至 /forms */}
          <Route path="forms" element={<DashboardPage />} />
          <Route path="submissions" element={<SubmissionsPage />} />
          <Route path="builder/new" element={
            <ProtectedRoute roles={['super_admin', 'dept_admin']}>
              <FormBuilderPage />
            </ProtectedRoute>
          } />
          <Route path="builder/:id" element={
            <ProtectedRoute roles={['super_admin', 'dept_admin']}>
              <FormBuilderPage />
            </ProtectedRoute>
          } />
          <Route path="fill/:id" element={<FormFillPage />} />
          <Route path="departments" element={
            <ProtectedRoute roles={['super_admin']}>
              <DepartmentsPage />
            </ProtectedRoute>
          } />
          <Route path="crm/connections" element={
            <ProtectedRoute permission="crm_connections">
              <CrmConnectionsPage />
            </ProtectedRoute>
          } />
          <Route path="crm/mapping" element={
            <ProtectedRoute permission="crm_mapping">
              <CrmMappingPage />
            </ProtectedRoute>
          } />
          <Route path="crm/jobs" element={
            <ProtectedRoute permission="crm_jobs">
              <CrmJobsPage />
            </ProtectedRoute>
          } />
          <Route path="linebot" element={
            <ProtectedRoute permission="linebot_manage">
              <LineBotPage />
            </ProtectedRoute>
          } />
          <Route path="calendar" element={
            <ProtectedRoute permission="calendar_manage">
              <CalendarPage />
            </ProtectedRoute>
          } />
          <Route path="knowledge" element={
            <ProtectedRoute permission="knowledge_manage">
              <KnowledgeBasePage />
            </ProtectedRoute>
          } />
          <Route path="contacts" element={
            <ProtectedRoute permission="contacts_manage">
              <RequireModule module="automotive" title="尚未開通賣車功能"><ContactsPage /></RequireModule>
            </ProtectedRoute>
          } />
          <Route path="pipeline" element={
            <ProtectedRoute roles={['super_admin', 'dept_admin', 'manager']}>
              <PipelinePage />
            </ProtectedRoute>
          } />
          <Route path="my-deals" element={<MyDealsPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
